import { parseReadingDocument, readingSourceIndex } from '@mio/shared';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { scrollPaneToTop, scrollWithinPane } from '../../lib/scroll-within-pane';
import { Button } from '../button';
import { SelectField } from '../select';

import { Markdown } from './markdown';
import { headingsOf } from './outline';

import type { ReactNode } from 'react';

type Document = NonNullable<ReturnType<typeof parseReadingDocument>>;
export type ReadingSectionFooter = (
  section: Document['sections'][number],
  index: number,
  total: number,
) => ReactNode;

function currentSection(document: Document, targets: Record<string, string>) {
  const query = new URLSearchParams(location.search).get('section');
  if (query === 'all') return 'all';
  const anchor = location.hash.slice(1);
  return (
    (Object.hasOwn(targets, anchor) ? targets[anchor] : undefined) ??
    document.sections.find((section) => section.id === query)?.id ??
    document.sections[0]?.id ??
    'all'
  );
}

function SectionedDocument({
  document,
  source,
  renderSectionFooter,
  workshopScope,
}: {
  document: Document;
  source: string;
  renderSectionFooter: ReadingSectionFooter | undefined;
  workshopScope: string | undefined;
}) {
  const targets = useMemo(
    () =>
      Object.fromEntries(
        [
          ...headingsOf(source).map((heading) => ({
            id: heading.id,
            sourceLine: heading.sourceLine,
          })),
          ...readingSourceIndex(source).fences.map((fence) => ({
            id: fence.id,
            sourceLine: fence.sourceLine,
          })),
        ].flatMap((target) => {
          const section = document.sections
            .filter((item) => item.sourceStartLine <= target.sourceLine)
            .at(-1);
          return section ? [[target.id, section.id]] : [];
        }),
      ),
    [document, source],
  );
  const [selected, setSelected] = useState(() => currentSection(document, targets));
  const root = useRef<HTMLElement>(null);
  const moved = useRef(false);
  const selectFocus = useRef<HTMLElement | null>(null);
  const position = document.sections.findIndex((section) => section.id === selected);
  const active = document.sections[position];
  const workshop = document.kind === 'workshop';
  // A workshop in the standard template opens with an un-numbered "Before you start" page.
  const templated = workshop && document.sections[0]?.id === 'overview';
  const milestones = templated ? document.sections.length - 1 : document.sections.length;
  const scope = templated ? (workshopScope ?? 'workshop') : undefined;
  const sectionLabel = (section: Document['sections'][number], index: number) =>
    templated
      ? index === 0
        ? 'Before you start'
        : `${index}. ${section.title}`
      : `${index + 1}. ${section.title}`;
  useEffect(() => {
    const update = () => setSelected(currentSection(document, targets));
    window.addEventListener('popstate', update);
    window.addEventListener('hashchange', update);
    window.addEventListener('mio:reading-navigate', update);
    return () => {
      window.removeEventListener('popstate', update);
      window.removeEventListener('hashchange', update);
      window.removeEventListener('mio:reading-navigate', update);
    };
  }, [document, targets]);
  useLayoutEffect(() => {
    if (moved.current) {
      moved.current = false;
      const reader = root.current;
      if (!reader) return;
      reader.focus({ preventScroll: true });
      // Content height and popup focus restoration can settle after this commit.
      // Start one scroll after layout; an immediate reset would bypass or restart its animation.
      const frame = requestAnimationFrame(() => scrollPaneToTop(reader));
      return () => cancelAnimationFrame(frame);
    } else {
      const anchor = location.hash.slice(1);
      if (anchor) {
        const target = Array.from(root.current?.querySelectorAll<HTMLElement>('[id]') ?? []).find(
          (element) => element.id === anchor,
        );
        if (target) scrollWithinPane(target, { block: 'start' });
      }
    }
  }, [selected]);
  function choose(id: string) {
    if (id === selected) return;
    const url = new URL(location.href);
    url.searchParams.set('section', id);
    url.hash = '';
    history.replaceState(history.state, '', url);
    moved.current = true;
    setSelected(id);
  }
  return (
    <section
      ref={root}
      data-reading-kind={document.kind}
      data-reading-targets={JSON.stringify(targets)}
      data-reading-section-id={selected}
      data-reading-section-title={active?.title ?? 'Full document'}
      tabIndex={-1}
      className="scroll-mt-5 outline-none"
      aria-label={workshop ? 'Build milestones' : 'Reading sections'}
    >
      <div className="border-line bg-surface mb-8 rounded-md border p-4">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-xs">
          <span className="text-fg font-semibold">
            {active
              ? templated && position === 0
                ? 'Before you start'
                : `${workshop ? 'Milestone' : 'Section'} ${templated ? position : position + 1} of ${milestones}`
              : `All ${milestones} ${workshop ? 'milestones' : 'sections'}`}
          </span>
          <Button
            variant="ghost"
            size="sm"
            type="button"
            onClick={() => choose(active ? 'all' : (document.sections[0]?.id ?? 'all'))}
          >
            {active
              ? workshop
                ? 'Read full brief'
                : 'Read full lesson'
              : 'Read one section at a time'}
          </Button>
        </div>
        <label className="text-fg-muted block text-xs">
          {workshop ? 'Jump to milestone' : 'Jump to section'}
          <SelectField
            className="mt-2 w-full"
            aria-label={workshop ? 'Jump to milestone' : 'Jump to section'}
            value={selected}
            onValueChange={(id) => {
              if (id !== selected) selectFocus.current = root.current;
              choose(id);
            }}
            finalFocus={() => {
              const target = selectFocus.current;
              selectFocus.current = null;
              return target ?? true;
            }}
            options={[
              ...document.sections.map((section, index) => ({
                value: section.id,
                label: sectionLabel(section, index),
              })),
              { value: 'all', label: workshop ? 'Full workshop brief' : 'Full lesson' },
            ]}
          />
        </label>
        <p className="text-fg-muted mt-3 text-sm leading-6">
          {active?.purpose ?? 'The complete document, with every explanation and example.'}
        </p>
      </div>
      {active ? (
        <>
          <Markdown
            key={selected}
            source={active.body}
            sourceStartLine={active.sourceStartLine}
            canonicalSource={source}
            workshopScope={scope}
          />
          {renderSectionFooter?.(active, position, document.sections.length)}
        </>
      ) : renderSectionFooter ? (
        document.sections.map((section, index) => (
          <div key={section.id}>
            <Markdown
              source={section.body}
              sourceStartLine={section.sourceStartLine}
              canonicalSource={source}
              workshopScope={scope}
            />
            {renderSectionFooter(section, index, document.sections.length)}
          </div>
        ))
      ) : (
        <Markdown key={selected} source={source} />
      )}
      {active ? (
        <nav
          className="border-line mt-8 flex flex-wrap justify-between gap-3 border-t pt-4"
          aria-label={workshop ? 'Milestone navigation' : 'Section navigation'}
        >
          <Button
            variant="ghost"
            size="sm"
            type="button"
            disabled={position === 0}
            onClick={() => choose(document.sections[position - 1]?.id ?? selected)}
          >
            Previous {workshop ? 'milestone' : 'section'}
          </Button>
          {position < document.sections.length - 1 ? (
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="h-auto whitespace-normal text-left"
              onClick={() => choose(document.sections[position + 1]?.id ?? selected)}
            >
              Next {workshop ? 'milestone' : 'section'}: {document.sections[position + 1]?.title}
            </Button>
          ) : (
            <span className="text-fg-muted py-2 text-sm">
              {workshop
                ? 'Final milestone · demonstrate your working application.'
                : 'Final section · carry these ideas into the workshop.'}
            </span>
          )}
        </nav>
      ) : null}
    </section>
  );
}

export function ReadingDocument({
  source,
  renderSectionFooter,
  renderDocumentFooter,
  workshopScope,
}: {
  source: string;
  renderSectionFooter?: ReadingSectionFooter;
  renderDocumentFooter?: () => ReactNode;
  /** Keys a template workshop's Done-when ticks, usually the activity ID. */
  workshopScope?: string;
}) {
  let document: ReturnType<typeof parseReadingDocument>;
  try {
    document = parseReadingDocument(source);
  } catch {
    return (
      <>
        <p role="alert">Section navigation is unavailable. The full reading follows.</p>
        <Markdown source={source} />
        {renderDocumentFooter?.()}
      </>
    );
  }
  // Selection belongs to this authored document. A different source must resolve its own
  // URL section before rendering; ordinary rerenders of the same source keep local state.
  return document ? (
    <SectionedDocument
      key={source}
      source={source}
      document={document}
      renderSectionFooter={renderSectionFooter}
      workshopScope={workshopScope}
    />
  ) : (
    <>
      <Markdown source={source} />
      {renderDocumentFooter?.()}
    </>
  );
}
